import { ReactNode, useCallback, useRef, useState } from "react";
import { Form, notification } from "antd";
import type { FormInstance } from "antd/es/form";

import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { Loading, LoadingSize } from "@/shared/components/Loading/Loading";
import { SingleFileDragger } from "@/ui-components/commonComponents/SingleFileDragger/SingleFileDragger";
import { useBeforeunload } from "@/shared/hooks/use-before-unload";
import { ValidationError } from "@/shared/interfaces/validation-error.interface";
import { ValidationErrorsModal } from "@/shared/components/ValidationErrorsModal/ValidationErrorsModal";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { FORM_VALIDATION_ERROR_MESSAGE } from "@/shared/constants/form.constants";

import { TestSelector } from "./TestSelector";
import { CompositeFormulasSection } from "./CompositeFormulasSection";
import { TemplateTestDetails } from "./useTemplateTestDetails";
import { buildTestListValidationRules } from "./testListValidationRules";
import { TemplateFormData } from "./types";

import style from "./style.module.css";

const TEST_SELECTOR_ID = "testSelector";

const UNEXPECTED_ERROR_MESSAGE = "An unexpected error occurred while generating the template. Please try again.";

export type SubmitOutcome = { kind: "success" } | { kind: "validation-failure"; errors: ValidationError[] };

interface TemplateFormProps {
  pageTitle: string;
  submittingTitle: string;
  errorModalTitle: string;
  form: FormInstance<TemplateFormData>;
  testDetails: TemplateTestDetails;
  initialValues: Partial<TemplateFormData>;
  onSubmit: (values: TemplateFormData) => Promise<SubmitOutcome>;
  children: ReactNode;
}

export function TemplateForm({
  pageTitle,
  submittingTitle,
  errorModalTitle,
  form,
  testDetails,
  initialValues,
  onSubmit,
  children,
}: TemplateFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationError[] | null>(null);
  const isSubmittingRef = useRef(false);

  useBeforeunload(
    useCallback((event: BeforeUnloadEvent) => {
      if (isSubmittingRef.current) {
        event.preventDefault();
      }
    }, []),
  );

  const handleSubmit = async (values: TemplateFormData) => {
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    setErrors(null);

    try {
      const outcome = await onSubmit(values);
      if (outcome.kind === "validation-failure") {
        setErrors(outcome.errors);
      }
    } catch (error) {
      notification.error({ message: getFriendlyErrorMessage(error) ?? UNEXPECTED_ERROR_MESSAGE });
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="main-container">
      <SEO titleParams={["Assessment Templates", pageTitle]} />

      {isSubmitting && (
        <div className={style.loadingContainer}>
          <Loading size={LoadingSize.default} />
          <p className={style.loadingTitle}>{submittingTitle}</p>
          <p className={style.loadingSubtext}>Processing your upload, do not close this tab</p>
        </div>
      )}

      <div className="inner-container" hidden={isSubmitting}>
        <h1 className={style.pageTitle}>{pageTitle}</h1>
        <div className={style.formCard}>
          <Form
            className={style.form}
            form={form}
            onFinish={handleSubmit}
            scrollToFirstError={{ block: "center" }}
            onFinishFailed={() => {
              notification.error({ message: FORM_VALIDATION_ERROR_MESSAGE });
            }}
            // Form.List rules only re-run when the list shape changes (add/remove/move),
            // not when a row's inner value changes — re-validate so list-level errors clear.
            onValuesChange={(changedValues) => {
              if ("tests" in changedValues) {
                form.validateFields([["tests"]]);
              }
            }}
            layout="vertical"
            requiredMark={false}
            initialValues={initialValues}
          >
            {children}

            <Form.List name="tests" rules={buildTestListValidationRules(testDetails)}>
              {(fields, { add, remove, move }, { errors: listErrors }) => (
                <div className={style.testSelectorField}>
                  <label htmlFor={TEST_SELECTOR_ID} className={style.testSelectorLabel}>
                    Select Test(s)
                  </label>
                  <TestSelector
                    id={TEST_SELECTOR_ID}
                    fields={fields}
                    addTestToList={add}
                    removeTestsFromList={remove}
                    moveTestInList={move}
                    hasListError={listErrors.length > 0}
                    testDetails={testDetails}
                  />
                  <Form.ErrorList errors={listErrors} />
                </div>
              )}
            </Form.List>

            <CompositeFormulasSection testDetails={testDetails} />

            <Form.Item
              name="zipFile"
              label="Upload Template Details"
              valuePropName="file"
              rules={[{ required: true, message: "Please upload a ZIP file." }]}
            >
              <SingleFileDragger accept=".zip" hintText="Support for a single ZIP file upload" />
            </Form.Item>

            <SubmitButton arrowAfter={true}>Submit</SubmitButton>
          </Form>
        </div>
      </div>

      <ValidationErrorsModal title={errorModalTitle} errors={errors} onClose={() => setErrors(null)} />
    </div>
  );
}
